import React, {
	useCallback,
	useMemo,
} from 'react';
import {
	useQuery,
	useQueryClient,
} from 'react-query';
import {
	Flex,
	Typography,
} from 'antd';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowsRotate } from '@fortawesome/pro-thin-svg-icons';
import { Link } from 'react-router-dom';
import {
	DATE_AND_TIME,
	FixtureTypes,
} from '@shared/utils/constants';
import { formatCurrency } from '@shared/utils/currency';
import { fixtureTypeToName } from '@shared/utils/fixtureUtils';
import { formatDate } from '@shared/utils/date';
import { formatAndRoundNumber } from '@shared/utils/formatNumber';
import type { VoyageData } from '@api/features/voyages/project/getProject';
import {
	deleteCacheEntry,
	getProject,
} from '@client/lib/api';
import MatrixTable from '@client/components/Table/MatrixTable';
import Button from '@client/components/Button';
import LoadingIndicator from '@client/components/LoadingIndicator';
import Card from '@client/components/Card/Card';
import { Links } from '@client/utils/links';
import { useVoyage } from '../VoyageProvider/VoyageProvider';
import styles from './ProjectView.module.css';

const VC_REVENUE_KEYS = ['freight', 'demurrage', 'despatch'];
const TC_REVENUE_KEYS = ['hire'];

const ProjectView = () => {
	const {
		voyageId,
	} = useVoyage();

	const queryClient = useQueryClient();

	const {
		data,
		isLoading,
		isFetching,
	} = useQuery(
		['project', voyageId],
		() => getProject(voyageId),
		{
			refetchOnWindowFocus: false,
			refetchOnReconnect: false,
			enabled: voyageId != null,
		},
	);

	const getHeader = useCallback((voyageData: VoyageData) => {
		if (voyageData.id === -1) {
			return (
				<div
					className={classNames(styles.headerContainer, styles.netHeaderContainer)}
				>
					<Typography.Title level={4} className={styles.netColumn}>
						Net
					</Typography.Title>
				</div>
			);
		}

		if (voyageData.voyage == null) {
			return <></>;
		}

		const fixtureName = voyageData.fixture?.type != null ?
			`${fixtureTypeToName(voyageData.fixture.type)}` : null;

		const date = voyageData.date != null ? formatDate(voyageData.date, 'DD/MM/YYYY') : '';
		const owner = voyageData.counterpartyName != null ? voyageData.counterpartyName : '';
		let formattedOwner = owner;

		if (formattedOwner.length > 16) {
			formattedOwner = `${owner.slice(0, 16)} [...]`;
		}

		const { name } = voyageData;

		return (
			<div className={styles.headerContainer}>
				<Typography.Text className={styles.headerLabel}>
					{fixtureName}
				</Typography.Text>
				<Link to={Links.Voyage.get(voyageData.voyage.id)}>{name}</Link>
				<Typography.Text className={styles.headerLabel}>
					{date}
				</Typography.Text>
				<Typography.Text strong>
					{formattedOwner}
				</Typography.Text>
			</div>
		);
	}, []);

	const transformedData = useMemo(() => {
		if (data == null) {
			return [];
		}

		const mappedData = data.data.map((d) => ({
			...d,
			width: 180,
			label: getHeader(d),
			sticky: d.fixture?.type === FixtureTypes.TC_IN,
		}));

		const tcIn = mappedData.find((d) => d.fixture?.type === FixtureTypes.TC_IN);

		if (tcIn == null) {
			return mappedData;
		}

		return [
			...mappedData.filter((d) => d.fixture?.type !== FixtureTypes.TC_IN),
			tcIn,
		];
	}, [data, getHeader]);
	
	const renderOutCell = useCallback((
		value: number,
		col: VoyageData,
		keys: string[],
		type: 'currency' | 'days' = 'currency',
		isNetRow: boolean = false,
		bold?: boolean,
		highlight?: boolean,
	) => {
		const [key] = keys.slice(-1);

		if (isNetRow && col.id !== -1) {
			if (highlight) {
				return (<div className={styles.highlightPnlTable}>-</div>);
			}

			return bold ? (<b>-</b>) : '-';
		}

		const fixtureType = col?.fixture?.type;

		if (fixtureType === FixtureTypes.TC_IN) {
			if (highlight) {
				return (<div className={styles.highlightPnlTable}>-</div>);
			}

			return bold ? (<b>-</b>) : '-';
		}

		if (
			fixtureType !== FixtureTypes.SPOT &&
            (VC_REVENUE_KEYS.includes(key)) && col.id !== -1
		) {
			return bold ? (<b>-</b>) : '-';
		}

		if (
			fixtureType !== FixtureTypes.TC_OUT &&
            (TC_REVENUE_KEYS.includes(key)) && col.id !== -1
		) {
			return bold ? (<b>-</b>) : '-';
		}

		if (type === 'days') {
			return bold ? (<b>{`${formatAndRoundNumber(value, 2)} Days`}</b>) : `${formatAndRoundNumber(value, 2)} Days`;
		}

		if (col.currency != null) {
			let formattedValue: React.ReactElement | string = formatCurrency(value, col.currency, { forceDecimals: true });

			if (bold) {
				formattedValue = (<b>{formattedValue}</b>);
			}

			if (highlight) {
				formattedValue = (<div className={styles.highlightPnlTable}>{formattedValue}</div>);
			}

			return formattedValue;
		}

		return '';
	}, []);

	const renderInCell = useCallback((value: number, col: VoyageData) => {
		const fixtureType = col?.fixture?.type;

		const showsHireCost =
			fixtureType === FixtureTypes.TC_IN ||
			fixtureType === FixtureTypes.TC_OUT ||
			fixtureType === FixtureTypes.BB_OUT ||
			fixtureType === FixtureTypes.SPOT;

		if (!showsHireCost && col.id !== -1) {
			return '-';
		}

		if (col.currency != null) {
			return formatCurrency(value, col.currency, { forceDecimals: true });
		}

		return '';
	}, []);

	const refresh = async () => {
		await deleteCacheEntry(`/api/voyages/project/${voyageId}?voyageId=${voyageId}`);
		await queryClient.invalidateQueries('project');
	};

	if (isLoading || isFetching || data == null) {
		return (
			<Flex vertical justify="center" align="center" gap={5}>
				<LoadingIndicator fullHeight size="large" />
				<Typography.Title level={3}>Loading project view..</Typography.Title>
			</Flex>
		);
	}

	return (
		<Card padding={12} className={styles.card}>
			<Flex
				vertical
				className={styles.refreshContainer}
			>
				<Typography.Text className={styles.smallText} type="secondary">
					Last updated:
					{' '}
					{data?.lastUpdated?.local().format(DATE_AND_TIME)}
				</Typography.Text>
				<Button
					onClick={refresh}
					className={classNames([styles.activeEntry, styles.refreshBtn])}
					icon={(
						<FontAwesomeIcon icon={faArrowsRotate} />
					)}
				>
					Refresh data
				</Button>
			</Flex>
			<div className={styles.container}>
				<MatrixTable
					tableWidth="100%"
					groupTitleColumnWidth={100}
					titleColumnWidth={165}
					data={transformedData}
					groups={[
						{
							title: (<Typography.Text strong>Revenue</Typography.Text>),
							children: [
								{ title: 'Freight', key: ['pnl', 'revenue', 'freight'], render: renderOutCell },
								{ title: 'Demurrage', key: ['pnl', 'revenue', 'demurrage'], render: renderOutCell },
								{ title: 'Despatch', key: ['pnl', 'revenue', 'despatch'], render: renderOutCell },
								{ title: 'Hire', key: ['pnl', 'revenue', 'hire'], render: renderOutCell },
								{ title: 'Other Revenue', key: ['pnl', 'revenue', 'otherRevenue'], render: renderOutCell },
								{ title: 'Address Commission', key: ['pnl', 'revenue', 'addComm'], render: renderOutCell },
								{ title: 'Brokers Commission', key: ['pnl', 'revenue', 'brokersComm'], render: renderOutCell },
								{ title: (<b>Net Revenue</b>),
									key: ['pnl', 'revenue', 'netRevenue'],
									render: (
										value: number,
										col: VoyageData,
										keys: string[],
										type: 'currency' | 'days' = 'currency',
										isNetRow: boolean = false,
									) => renderOutCell(value, col, keys, type, isNetRow, true) },
							],
						},
						{
							title: (<Typography.Text strong>Expenses</Typography.Text>),
							children: [
								{ title: 'Port Expenses', key: ['pnl', 'expenses', 'portExpenses'], render: renderOutCell },
								{ title: 'Bunker Expenditure', key: ['pnl', 'expenses', 'bunkerExpenditure'], render: renderOutCell },
								{ 
									title: 'TC Out Delivery Bunker Gain / Loss', 
									key: ['pnl', 'expenses', 'tcOutDeliveryBunkerGainLoss'], 
									render: renderOutCell 
								},
								{ 
									title: 'TC In Redelivery Bunker Gain / Loss', 
									key: ['pnl', 'expenses', 'tcInRedeliveryBunkerGainLoss'], 
									render: renderOutCell 
								},
								{ title: 'Other Expenses', key: ['pnl', 'expenses', 'otherExpenses'], render: renderOutCell },
								{ title: 'EU ETS', key: ['pnl', 'expenses', 'euEts'], render: renderOutCell },
								{ title: 'FuelEU', key: ['pnl', 'expenses', 'fuelEuPenalty'], render: renderOutCell },
								{ title: 'Liner Costs', key: ['pnl', 'expenses', 'linerCosts'], render: renderOutCell },
								{ title: 'Off-Hire', key: ['pnl', 'expenses', 'offHire'], render: renderOutCell },
								{ title: (<b>Total Expenses</b>),
									key: ['pnl', 'expenses', 'totalExpenses'],
									render: (
										value: number,
										col: VoyageData,
										keys: string[],
										type: 'currency' | 'days' = 'currency',
										isNetRow: boolean = false,
									) => renderOutCell(value * -1, col, keys, type, isNetRow, true) },
							],
						},
						{
							title: (<Typography.Text strong>Voy. Result</Typography.Text>),
							children: [
								{ title: (<b>TCE Earnings</b>),
									key: ['pnl', 'voyageResult', 'tce'],
									render: (
										value: number,
										col: VoyageData,
										keys: string[],
										isNetRow: boolean = false,
									) => renderOutCell(value, col, keys, 'currency', isNetRow, true) },
								{
									title: (<b>Total Days</b>),
									key: ['pnl', 'voyageResult', 'totalDays'],
									render: (
										value: number,
										col: VoyageData,
										keys: string[],
										isNetRow: boolean = false,
									) => renderOutCell(value, col, keys, 'days', isNetRow, true),
								},
								{ title: (<div className={styles.highlightPnlTable}>TCE / Day</div>),
									key: ['pnl', 'voyageResult', 'tcePerDay'],
									render: (
										value: number,
										col: VoyageData,
										keys: string[],
										type: 'currency' | 'days' = 'currency',
										isNetRow: boolean = false,
									) => renderOutCell(value, col, keys, type, isNetRow, false, true) },
							],
						},
						{
							title: (<Typography.Text strong>Hire Cost</Typography.Text>),
							children: [
								{ title: 'Gross Hire Cost', key: ['pnl', 'hireCosts', 'grossHireCost'], render: renderInCell },
								{ title: 'Other Costs', key: ['pnl', 'hireCosts', 'otherCosts'], render: renderInCell },
								{ title: 'Address Commission', key: ['pnl', 'hireCosts', 'addComm'], render: renderInCell },
								{ title: 'Broker Commission', key: ['pnl', 'hireCosts', 'brokersComm'], render: renderInCell },
								{ title: 'Off-Hire', key: ['pnl', 'hireCosts', 'offHire'], render: renderInCell },
								{ title: 'Total Hire Cost', key: ['pnl', 'hireCosts', 'totalHireCost'], render: renderInCell },
							],
						},
						{
							title: (<Typography.Text strong>Net Result</Typography.Text>),
							children: [
								{
									title: (<b>Net Result</b>),
									key: ['pnl', 'netResult', 'netResult'],
									render: (
										value: number,
										col: VoyageData,
										keys: string[],
									) => renderOutCell(value, col, keys, 'currency', true, true),
								},
								{ title: (<div className={styles.highlightPnlTable}>Net Result / Day</div>),
									key: ['pnl', 'netResult', 'netResultPerDay'],
									render: (
										value: number,
										col: VoyageData,
										keys: string[],
									) => renderOutCell(value, col, keys, 'currency', true, false, true) },
							],
						},
					]}
				/>
			</div>
		</Card>
	);
};

export default ProjectView;
